import { useState, useEffect, useCallback, useRef, useMemo, useId } from 'react'
import { supabase } from '../lib/supabase'
import { getCached, setCached } from '../lib/queryCache'
import * as entityCache from '../lib/entityCache'
import { useAutoRefresh } from './useAutoRefresh'
import { getSessionWithTimeout } from '../lib/authSession'
import { localFallback, localUpsertMany } from '../lib/syncEngine'
import { listStatusesFor, folderShowsUnanalyzed, mergeUnanalyzed, UNANALYZED_STATUSES } from '../lib/inboxFolders'
import { parseInboxQuery, localCanHonor, applyToIndexedQuery, rowMatches, rowMatchesTerms } from '../lib/inboxSearch'

// Default page size. Focused inbox uses 100 to give a full triage window.
const DEFAULT_PAGE_SIZE = 50
const FOCUSED_PAGE_SIZE = 100

// ─── Search ──────────────────────────────────────────────────────────────────
// One reading of the query for every source: src/lib/inboxSearch.js. A query
// the local index cannot honour (to:, label:, filename:, OR, …) returns no
// indexed rows — Gmail's own search answers it — rather than a looser match.
const NO_ROWS_ID = '00000000-0000-0000-0000-000000000000'

// ─── Snooze (server-backed with localStorage fallback) ──────────────────────
// The DB column (email_intelligence.snoozed_until) is the source of truth so
// snoozes travel across devices; localStorage is kept as an instant local
// filter and as the fallback until the 20260707_snoozed_until migration runs.
// Column presence is feature-detected once per session, so deploy order
// doesn't matter.
const SNOOZE_KEY = 'chatrone.inbox.snoozed'

let _snoozeColumnKnown = null
async function snoozeColumnAvailable() {
  if (_snoozeColumnKnown !== null) return _snoozeColumnKnown
  try {
    const { error } = await supabase.from('email_intelligence').select('snoozed_until').limit(1)
    _snoozeColumnKnown = !error
  } catch { _snoozeColumnKnown = false }
  return _snoozeColumnKnown
}

// saved_emails.in_inbox (migration 20261023) — Gmail's confirmed INBOX
// membership for not-yet-analyzed mail, independent of the labels array
// (inboxFolders.js). Feature-detected once per session like snoozed_until
// above, so the unanalyzed-mail read still works before the migration runs.
let _inInboxColumnKnown = null
async function inInboxColumnAvailable() {
  if (_inInboxColumnKnown !== null) return _inInboxColumnKnown
  try {
    const { error } = await supabase.from('saved_emails').select('in_inbox').limit(1)
    _inInboxColumnKnown = !error
  } catch { _inInboxColumnKnown = false }
  return _inInboxColumnKnown
}

function _getSnoozed() {
  try { return JSON.parse(localStorage.getItem(SNOOZE_KEY) || '{}') } catch { return {} }
}

export function snoozeEmail(id, untilIso) {
  const data = _getSnoozed()
  data[id] = untilIso
  localStorage.setItem(SNOOZE_KEY, JSON.stringify(data))
  // Dispatch a storage event so other open tabs / the hook re-filters
  window.dispatchEvent(new Event('chatrone:snooze-changed'))
  snoozeColumnAvailable().then(ok => {
    if (!ok) return
    supabase.from('email_intelligence').update({ snoozed_until: untilIso }).eq('id', id)
      .then(() => {}, () => {})
  })
}

export function unsnoozeEmail(id) {
  const data = _getSnoozed()
  delete data[id]
  localStorage.setItem(SNOOZE_KEY, JSON.stringify(data))
  window.dispatchEvent(new Event('chatrone:snooze-changed'))
  snoozeColumnAvailable().then(ok => {
    if (!ok) return
    supabase.from('email_intelligence').update({ snoozed_until: null }).eq('id', id)
      .then(() => {}, () => {})
  })
}

function filterSnoozed(items) {
  const data = _getSnoozed()
  const now = Date.now()
  const expired = []
  const result = items.filter(item => {
    const until = data[item.id]
    if (!until) return true
    if (new Date(until).getTime() <= now) { expired.push(item.id); return true }
    return false
  })
  if (expired.length > 0) {
    expired.forEach(id => delete data[id])
    localStorage.setItem(SNOOZE_KEY, JSON.stringify(data))
  }
  return result
}

// ─── Desktop notification helper ─────────────────────────────────────────────

// Cache: user_id → short display name. Used to label notifications that are
// about ANOTHER principal's mailbox (Aaron gets notified about Kat's/Ari's mail
// because the realtime subscription below isn't always scoped to his user_id).
const _principalNameCache = {}
async function resolvePrincipalName(userId) {
  if (!userId) return null
  if (userId in _principalNameCache) return _principalNameCache[userId]
  try {
    const { data } = await supabase
      .from('user_profiles')
      .select('full_name, email')
      .eq('id', userId)
      .maybeSingle()
    const name = (data?.full_name ? data.full_name.split(' ')[0] : null)
      || (data?.email ? data.email.split('@')[0] : null)
      || null
    _principalNameCache[userId] = name
    return name
  } catch {
    return null
  }
}

async function fireDesktopNotification(email) {
  if (typeof window === 'undefined' || !('Notification' in window)) return
  if (Notification.permission !== 'granted') return
  const from = email.from_name || email.from_email || 'New email'
  const subject = email.subject || '(no subject)'

  // If this email lives in ANOTHER principal's mailbox, prefix the title so
  // Aaron knows it won't be in his own inbox and doesn't go hunting for it.
  let title = from
  try {
    const currentUid = await getCurrentUserId()
    const ownerUid = email.user_id
    if (ownerUid && currentUid && ownerUid !== currentUid) {
      const name = await resolvePrincipalName(ownerUid)
      title = `${name ? `${name}'s inbox` : 'Team inbox'} · ${from}`
    }
  } catch { /* fall back to the plain sender title */ }

  try {
    const n = new Notification(title, {
      body: subject,
      icon: '/logo-square.png',
      tag: email.id || email.gmail_message_id,
      silent: false,
    })
    setTimeout(() => n.close(), 6000)
  } catch { /* non-fatal */ }
}

// Trailing-edge debounce for Realtime callbacks. Rapid bursts (e.g. a
// newsletter digest delivery where 10 emails land in the same second,
// or a cron re-analyzing a batch with 20 UPDATEs back-to-back) collapse
// to a single fetch. 300ms is short enough to feel instant to a user
// but long enough to absorb typical bursts.
const REALTIME_DEBOUNCE_MS = 300

function useDebouncedCallback(fn, delay) {
  const timerRef = useRef(null)
  const fnRef = useRef(fn)
  fnRef.current = fn
  return useMemo(() => {
    const wrapped = (...args) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => { fnRef.current(...args) }, delay)
    }
    wrapped.cancel = () => { if (timerRef.current) clearTimeout(timerRef.current) }
    return wrapped
  }, [delay])
}

// Cache the current user ID to avoid re-fetching on every query
let _cachedUserId = null
async function getCurrentUserId() {
  if (_cachedUserId) return _cachedUserId
  // Timeout-guarded session read — NOT supabase.auth.getUser(), which makes its
  // own network round-trip and, like getSession(), can hang on the wedged auth
  // gate BEFORE any fetch-level timeout applies (the failure getAuthHeaders
  // already guards against — see authSession.js). Every inbox fetch awaits this
  // first, so an unguarded hang here froze the whole list on skeletons. On
  // timeout we return null (not cached, so it retries next call) and the query
  // falls back to RLS-only scoping rather than freezing.
  const { data: { session } } = await getSessionWithTimeout(5000)
  _cachedUserId = session?.user?.id || null
  return _cachedUserId
}

// ─── Helper: get auth headers ───────────────────────────────────────────────

async function getAuthHeaders() {
  // Use the timeout-guarded session read — a wedged supabase.auth.getSession()
  // (half-open socket after sleep) would otherwise hang the send BEFORE the
  // fetch fires, freezing the UI past any fetch-level timeout. On timeout we
  // fall through token-less; the server returns 401 and the caller surfaces it.
  const { data: { session } } = await getSessionWithTimeout(5000)
  const token = session?.access_token
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

// Google Calendar auto-generates these invitation / update / cancellation
// emails "from" the organizer, so when you create an event they land in the OS
// as your own outbound mail. They're informational, not sent-mail clutter, so
// the inbox tabs keep them visible even though is_outbound is true. Matched by
// the exact subject prefixes Google uses (English). Response emails
// ("Accepted:" / "Declined:") are usually inbound and already shown; we don't
// broaden to email_type='scheduling' because that also catches your own
// conversational "let's meet Tuesday" replies, which DO belong hidden.
const CALENDAR_INVITE_SUBJECT_PREFIXES = ['Invitation:', 'Updated invitation:', 'Canceled:', 'Cancelled:']
function isCalendarInviteSubject(subject) {
  const s = (subject || '').trimStart().toLowerCase()
  return CALENDAR_INVITE_SUBJECT_PREFIXES.some((p) => s.startsWith(p.toLowerCase()))
}

// ─── Offline fallback filtering ──────────────────────────────────────────────
// Mirror the Supabase list filters in JS so locally-synced PGlite rows can be
// served when the network query times out. PostgREST-only operators (full-text
// ranking, etc.) degrade to plain substring matches — this is a best-effort
// offline view, not an exact replica of the server query.
function applyLocalInboxFilters(rows, filters) {
  let out = Array.isArray(rows) ? rows : []

  const statuses = listStatusesFor(filters)
  out = out.filter(r => statuses.includes(r.status))

  // Mirrors the server query: search is mailbox-wide, so tab/folder scoping
  // only applies when not searching.
  if (!filters.search) {
    // Mirror the DB snooze exclusion (snoozed rows hide until they wake)
    out = out.filter(r => !r.snoozed_until || new Date(r.snoozed_until).getTime() <= Date.now())
    if (filters.category)      out = out.filter(r => r.category === filters.category)
    if (filters.urgency)       out = out.filter(r => r.urgency === filters.urgency)
    if (filters.email_type)    out = out.filter(r => r.email_type === filters.email_type)
    if (filters.triage_status) out = out.filter(r => r.triage_status === filters.triage_status)
    if (filters.unread)        out = out.filter(r => r.is_read === false)

    // Mirrors the Sent / Starred / Label / default-exclude-outbound branch.
    if (filters.is_outbound === true)     out = out.filter(r => r.is_outbound === true)
    else if (filters.is_starred === true) out = out.filter(r => r.is_starred === true)
    else if (filters.label)               out = out.filter(r => Array.isArray(r.labels) && r.labels.includes(filters.label))
    else if (filters.archived === true)   out = out.filter(r => !r.is_outbound)
    // default: hide outbound — except calendar invitations you created, which
    // are informational (mirrors the server query's invite-subject OR).
    else                                  out = out.filter(r => !r.is_outbound || isCalendarInviteSubject(r.subject))
  }

  if (filters.search) {
    const parsed = parseInboxQuery(filters.search)
    if (!localCanHonor(parsed)) return []
    out = out.filter(r => rowMatches(r, parsed) && rowMatchesTerms(r, parsed))
  }

  // Newest first, matching the server's `sent_at desc nulls last`.
  return out.sort((a, b) => String(b.sent_at || '').localeCompare(String(a.sent_at || '')))
}

// Every column of email_intelligence EXCEPT raw_analysis — the full Claude
// response blob, the single heaviest column (~28 KB per 100 rows, over 12% of
// the list payload) and referenced nowhere in src/. A focused page is 100 rows,
// so `select('*')` was shipping it on every poll, focus and Realtime refresh.
// Listed explicitly because PostgREST has no "all but" syntax; if you add a
// column that the list or detail pane needs, add it here too.
const LIST_COLUMNS = [
  'id', 'gmail_message_id', 'gmail_thread_id', 'user_id', 'subject',
  'from_email', 'from_name', 'to_emails', 'cc_emails', 'urgency', 'email_type',
  'priority_score', 'circle_of_influence', 'suggested_contact_ids',
  'suggested_deal_ids', 'suggested_project_ids', 'suggested_property_ids',
  'suggested_opportunity_ids', 'confirmed_links', 'draft_reply', 'reply_tone',
  'reply_status', 'extracted_actions', 'summary', 'sentiment',
  'relationship_signal', 'status', 'processing_error', 'tokens_used',
  'model_used', 'processed_at', 'reviewed_at', 'reviewed_by', 'created_at',
  'updated_at', 'is_read', 'category', 'sent_at', 'triage_status', 'flag_color',
  'requires_your_action', 'delegated_to_user_id', 'triaged_at', 'triaged_by',
  'is_outbound', 'is_starred', 'labels', 'deal_suggestion', 'deal_suggestion_at',
  'has_attachments', 'attachments_auto_filed', 'attachments_filed_at',
  'attachments_needs_review', 'snoozed_until', 'source',
].join(',')

// ─── Server list query builder ───────────────────────────────────────────────
// One place for the inbox list's filter → PostgREST translation, shared by the
// page-0 fetch and loadMore (keyset pagination). Caller adds range/limit.
// MUST stay synchronous: a PostgrestFilterBuilder is a thenable, so if this
// function were async, `await buildListQuery(...)` would RESOLVE the builder —
// executing the unbounded query and handing callers the result object, whose
// missing .range()/.lt() then throws on every fetch (the July 2026 frozen-inbox
// bug). The async snooze-column probe is the caller's job (dbSnooze param).
function buildListQuery(userId, filters, dbSnooze = false, excludeIcloud = false) {
  let q = supabase
    .from('email_intelligence')
    .select(LIST_COLUMNS, { count: 'estimated' })
    .order('sent_at', { ascending: false, nullsFirst: false })

  if (userId) q = q.eq('user_id', userId)
  // View-as privacy: personal iCloud mail is visible only when Aaron himself is
  // signed in. In a supervisor "view-as" session (viewingUserId set), hide it.
  if (excludeIcloud) q = q.neq('source', 'icloud')
  // Hide DB-snoozed emails from inbox tabs (they live in the Snoozed
  // folder until they wake). Search still finds them — Gmail semantics.
  if (!filters.search && dbSnooze) {
    q = q.or(`snoozed_until.is.null,snoozed_until.lt.${new Date().toISOString()}`)
  }
  // status narrows the Inbox only ('archived' mirrors "INBOX label absent");
  // Starred / Sent / labels / search look across archived mail, Archive shows
  // it alone (src/lib/inboxFolders.js).
  q = q.in('status', listStatusesFor(filters))

  // Search is mailbox-wide (Gmail semantics): searching from any tab looks
  // across every category and folder, sent mail included — the tab/folder
  // scoping below only applies when NOT searching. The Gmail-archive pass
  // in the list component covers anything not yet ingested, so together
  // the two halves return one undifferentiated result set.
  if (!filters.search) {
    if (filters.category) {
      // Personal iCloud mail (category=null) should surface in the primary
      // Focused view — Aaron lands there and asked not to switch tabs to see
      // personal mail — and in the dedicated Personal tab. It's OR'd in there
      // (unless hidden by the view-as guard). Every other category tab keeps
      // its exact category match, so iCloud stays out of News/Updates/Promos.
      if (!excludeIcloud && (filters.category === 'focused' || filters.category === 'personal')) {
        q = q.or(`category.eq.${filters.category},source.eq.icloud`)
      } else {
        q = q.eq('category', filters.category)
      }
    }
    if (filters.urgency)    q = q.eq('urgency', filters.urgency)
    if (filters.email_type) q = q.eq('email_type', filters.email_type)
    if (filters.triage_status) q = q.eq('triage_status', filters.triage_status)
    if (filters.unread)     q = q.eq('is_read', false)

    // Sent folder / Starred folder / Label filter — Gmail-parity views.
    // When NONE of these are set, hide outbound from the normal inbox
    // tabs (Focused / Industry / etc.) — your own sent mail shouldn't
    // clutter the inbox. Thread view still shows outbound inline.
    if (filters.is_outbound === true) {
      q = q.eq('is_outbound', true)
    } else if (filters.is_starred === true) {
      q = q.eq('is_starred', true)
    } else if (filters.label) {
      q = q.contains('labels', [filters.label])
    } else if (filters.archived === true) {
      q = q.or('is_outbound.is.null,is_outbound.eq.false')
    } else {
      // default: exclude outbound from inbox tabs — EXCEPT calendar invitations
      // you created (Google sends those "from" you, but they're informational,
      // not sent-mail clutter). Match the invite subject prefixes.
      const inviteOr = CALENDAR_INVITE_SUBJECT_PREFIXES.map((p) => `subject.ilike."${p}*"`).join(',')
      q = q.or(`is_outbound.is.null,is_outbound.eq.false,${inviteOr}`)
    }
  }

  if (filters.search) {
    const parsed = parseInboxQuery(filters.search)
    q = localCanHonor(parsed) ? applyToIndexedQuery(q, parsed) : q.eq('id', NO_ROWS_ID)
  }

  return q
}

// ─── List hook ──────────────────────────────────────────────────────────────

export function useInboxIntelligence(filters = {}, page = 0, options = {}) {
  const { viewingUserId } = options
  // Focused inbox gets 100 so the full triage window is visible without pagination.
  // All other tabs use 50. Callers can override via options.pageSize.
  const pageSize = options.pageSize ?? (filters.category === 'focused' ? FOCUSED_PAGE_SIZE : DEFAULT_PAGE_SIZE)
  const cacheKey = `inbox:${viewingUserId || 'self'}:${JSON.stringify(filters)}:${page}:${pageSize}`
  const cached = getCached(cacheKey)
  const [items, setItems]       = useState(cached || [])
  const [total, setTotal]       = useState(0)
  const [loading, setLoading]   = useState(!cached)
  const [error, setError]       = useState(null)
  const [stats, setStats]       = useState({ respondToday: 0, respondThisWeek: 0, fyi: 0, unread: 0, total: 0, categoryUnread: {}, followUpPending: 0 })

  // Infinite scroll: rows older than the live page-0 window, loaded on demand
  // via keyset pagination (sent_at < oldest). Kept in a ref so silent
  // refreshes of page 0 don't wipe them; reset whenever the view changes.
  const extraRef = useRef([])
  const itemsRef = useRef(items)
  useEffect(() => { itemsRef.current = items }, [items])
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  useEffect(() => { extraRef.current = []; setHasMore(false) }, [cacheKey])

  // Resolved user ID stored in a ref so the Realtime effect can filter by it
  // without needing to be inside the async fetch callback.
  const resolvedUserIdRef = useRef(viewingUserId || null)

  // Newest request wins. A slow response for an older search or folder must
  // never overwrite the list for the current one: each fetch takes a number,
  // and only the latest number may write state.
  const reqSeq = useRef(0)
  const viewKeyRef = useRef(cacheKey)
  viewKeyRef.current = cacheKey

  const fetch = useCallback(async (silent = false) => {
    const seq = ++reqSeq.current
    const stale = () => seq !== reqSeq.current
    if (!silent && !getCached(cacheKey)) setLoading(true)
    setError(null)
    try {
      // Wrap every awaited Supabase call in a timeout so a hung connection (the
      // wedged-client failure mode that keeps navigator.locks disabled) can't
      // leave a folder stuck on skeleton loaders forever. On timeout the promise
      // rejects → catch/finally runs → setLoading(false), and the 30s silent
      // poll retries. Without this, a single hung query freezes the tab.
      const withTimeout = (promise, ms = 15000) =>
        Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))])

      // viewingUserId overrides the logged-in user — used by the "view-as"
      // mode where an authorized principal opens Janet's mailbox in
      // read-only form. When null/undefined, falls back to the caller.
      const userId = viewingUserId || await withTimeout(getCurrentUserId())
      resolvedUserIdRef.current = userId

      // Fetch all stats in a single RPC call (1 connection instead of 9)

      const [statsRes, followUpRes] = await Promise.all([
        withTimeout(supabase.rpc('get_inbox_stats', { p_user_id: userId })).catch(() => ({ data: null })),
        withTimeout(supabase
          .from('email_intelligence')
          .select('id', { count: 'exact', head: true })
          .eq('triage_status', 'pending_review')
          .eq('user_id', userId || '00000000-0000-0000-0000-000000000000')).catch(() => ({ count: 0 })),
      ])
      const statsData = statsRes.data
      const followUpPending = followUpRes.count || 0
      if (statsData) {
        setStats({
          respondToday: statsData.respondToday || 0,
          respondThisWeek: statsData.respondThisWeek || 0,
          review: statsData.review || 0,
          fyi: statsData.fyi || 0,
          unread: statsData.unread || 0,
          total: (statsData.respondToday || 0) + (statsData.respondThisWeek || 0) + (statsData.fyi || 0),
          categoryUnread: statsData.categoryUnread || {},
          followUpPending,
        })
      } else {
        setStats(s => ({ ...s, followUpPending }))
      }

      // Snoozed view — show ONLY currently-snoozed emails (Gmail's Snoozed
      // folder). DB column is the cross-device source of truth; localStorage
      // ids are unioned in for snoozes set before the migration ran (or while
      // the DB write was in flight).
      if (filters.snoozed === true) {
        const snoozeData = _getSnoozed()
        const now = Date.now()
        const activeIds = Object.keys(snoozeData).filter(id => new Date(snoozeData[id]).getTime() > now)
        const dbSnooze = await snoozeColumnAvailable()

        const byId = new Map()
        if (dbSnooze) {
          let dq = supabase.from('email_intelligence').select('*').gt('snoozed_until', new Date().toISOString())
          if (userId) dq = dq.eq('user_id', userId)
          const { data: dbRows } = await withTimeout(dq).catch(() => ({ data: null }))
          for (const r of dbRows || []) byId.set(r.id, r)
        }
        const missingLocal = activeIds.filter(id => !byId.has(id))
        if (missingLocal.length > 0) {
          let sq = supabase.from('email_intelligence').select('*').in('id', missingLocal)
          if (userId) sq = sq.eq('user_id', userId)
          const { data: localRows, error: sErr } = await withTimeout(sq)
          if (sErr && byId.size === 0) throw sErr
          for (const r of localRows || []) byId.set(r.id, { ...r, snoozed_until: r.snoozed_until || snoozeData[r.id] })
        }
        const rows = [...byId.values()]
          .sort((a, b) => new Date(a.snoozed_until || 0) - new Date(b.snoozed_until || 0))
        if (stale()) return
        setItems(rows); setTotal(rows.length); setCached(cacheKey, rows)
        lastFetchedAt.current = Date.now()
        return
      }

      // Fetch list with filters. The snooze-column probe is async (one-time,
      // cached) and must resolve BEFORE the builder is created — see the
      // buildListQuery comment for why the builder itself can't be awaited.
      const dbSnooze = await withTimeout(snoozeColumnAvailable()).catch(() => false)
      let q = buildListQuery(userId, filters, dbSnooze, !!viewingUserId)
        .range(page * pageSize, (page + 1) * pageSize - 1)

      const { data, count, error: err } = await withTimeout(q)
      if (stale()) return
      if (err) throw err

      // Write-through: mirror the freshly-fetched rows into PGlite so an
      // offline/timeout fallback (catch block below) can serve them. Raw rows
      // (pre-enrichment) keep the local copy clean of transient UI fields.
      // Fire-and-forget — never block the render on a local write.
      // One batched statement rather than a loop of ~100 single-row upserts —
      // PGlite is main-thread in the browser, and the per-row version ran on
      // every 30s poll, focus and Realtime burst, which is what made the list
      // periodically stall.
      if (Array.isArray(data) && data.length > 0) {
        localUpsertMany('email_intelligence', data).catch(() => {})
      }

      // Snoozed emails stay hidden from the inbox tabs but DO surface in
      // search results — matching Gmail, where search covers snoozed mail.
      let incoming = filters.search ? (data || []) : filterSnoozed(data || [])

      // Delivered but not analyzed yet: still mail. Read from saved_emails and
      // shown in its folder, marked "not analyzed yet" (inboxFolders.js). A
      // failed read here never hides the analyzed list — it is only logged.
      if (page === 0 && folderShowsUnanalyzed(filters)) {
        try {
          const hasInInbox = await inInboxColumnAvailable()
          let uq = supabase.from('saved_emails')
            .select(`gmail_message_id, gmail_thread_id, user_id, subject, sender_name, sender_email, to_emails, cc_emails, sent_at, is_read, is_outbound, labels, intelligence_status${hasInInbox ? ', in_inbox' : ''}`)
            .in('intelligence_status', UNANALYZED_STATUSES)
            .neq('source', 'icloud')
            .order('sent_at', { ascending: false, nullsFirst: false })
            .limit(50)
          if (userId) uq = uq.eq('user_id', userId)
          if (filters.is_outbound === true) uq = uq.eq('is_outbound', true)
          const { data: pendingRows, error: pendErr } = await withTimeout(uq, 8000)
          if (pendErr) console.warn('[inbox] unanalyzed mail read failed:', pendErr.message)
          else incoming = mergeUnanalyzed(incoming, pendingRows, filters)
        } catch (pe) {
          console.warn('[inbox] unanalyzed mail read failed:', pe?.message)
        }
      }

      // Post-fetch enrichment — both lookups are display-only and independent,
      // so they run in parallel instead of as two serial round-trips.
      //
      // 1. Sent tab: open/click counts from email_tracking_summary (view over
      //    email_tracking + email_tracking_events), joined on message_id =
      //    gmail_message_id (populated at send time by gmail-send.js).
      // 2. All tabs: thread message counts so the list shows "13" like Gmail.
      //    Prefers the get_thread_counts RPC (single GROUP BY in the DB);
      //    falls back to counting raw rows client-side if the RPC isn't
      //    deployed yet.
      const messageIds = filters.is_outbound === true
        ? incoming.map(i => i.gmail_message_id).filter(Boolean)
        : []
      const threadIds = [...new Set(incoming.filter(e => e.gmail_thread_id).map(e => e.gmail_thread_id))]

      // Fetch ALL tracking rows for these sent emails (not only opened ones) so the
      // list can show a hollow "sent, not opened yet" dot as well as a filled
      // "opened" dot. split_group_id lets the popover show per-recipient detail.
      const trackingPromise = messageIds.length > 0
        ? supabase
            .from('email_tracking_summary')
            .select('message_id, open_count, click_count, last_opened_at, split_group_id')
            .in('message_id', messageIds)
            .then(({ data }) => data || [])
        : Promise.resolve([])

      const threadCountPromise = threadIds.length > 0
        ? supabase.rpc('get_thread_counts', { p_thread_ids: threadIds }).then(async ({ data, error: rpcErr }) => {
            if (!rpcErr && data) {
              const map = {}
              data.forEach(r => { if (r.gmail_thread_id) map[r.gmail_thread_id] = r.msg_count })
              return map
            }
            // Fallback: RPC not deployed — count raw rows client-side
            const { data: threadRows } = await supabase
              .from('saved_emails')
              .select('gmail_thread_id')
              .in('gmail_thread_id', threadIds)
              .limit(2000)
            const map = {}
            ;(threadRows || []).forEach(r => { if (r.gmail_thread_id) map[r.gmail_thread_id] = (map[r.gmail_thread_id] || 0) + 1 })
            return map
          })
        : Promise.resolve({})

      try {
        const [tracks, countMap] = await Promise.all([trackingPromise, threadCountPromise])
        const trackMap = {}
        // A group-tracked send has one tracking row PER recipient sharing the
        // same message_id — aggregate so the dot reflects the whole group
        // (opened = anyone opened; count = total opens across recipients).
        tracks.forEach(t => {
          if (!t.message_id) return
          const prev = trackMap[t.message_id]
          if (!prev) { trackMap[t.message_id] = { ...t }; return }
          prev.open_count = (prev.open_count || 0) + (t.open_count || 0)
          prev.click_count = (prev.click_count || 0) + (t.click_count || 0)
          if (t.last_opened_at && (!prev.last_opened_at || t.last_opened_at > prev.last_opened_at)) {
            prev.last_opened_at = t.last_opened_at
          }
          prev.split_group_id = prev.split_group_id || t.split_group_id
        })
        incoming = incoming.map(item => {
          let next = item
          const t = item.gmail_message_id && trackMap[item.gmail_message_id]
          if (t) next = {
            ...next,
            is_tracked: true,
            has_been_opened: t.open_count > 0,
            open_count: t.open_count,
            click_count: t.click_count,
            last_opened_at: t.last_opened_at,
            split_group_id: t.split_group_id,
          }
          // Outbound row with no tracking record (sent before tracking, or off) —
          // mark it so the read-receipt dot renders "not tracked" without a per-row fetch.
          else if (item.is_outbound && messageIds.length > 0) next = { ...next, is_tracked: false }
          if (item.gmail_thread_id && countMap[item.gmail_thread_id] > 1) {
            next = next === item ? { ...next } : next
            next.thread_count = Math.min(countMap[item.gmail_thread_id], 99)
          }
          return next
        })
      } catch { /* non-fatal — badges and counts are display-only */ }

      if (stale()) return
      // Re-append any loadMore()-fetched older rows (deduped against the live
      // window) so background refreshes never truncate an extended list.
      if (extraRef.current.length > 0) {
        const liveIds = new Set(incoming.map(i => i.id))
        extraRef.current = extraRef.current.filter(r => !liveIds.has(r.id))
        incoming = [...incoming, ...extraRef.current]
      }

      // During silent refreshes, only replace state when something actually
      // changed — same IDs in same order with same updated_at means no
      // re-render needed, which eliminates the list flash on background polls.

      // NOTE: is_read / is_starred / status are compared explicitly. They are
      // toggled by paths that don't reliably move updated_at (there is no
      // updated_at trigger on email_intelligence), so an updated_at-only check
      // reported "same" and returned prev — the row kept rendering as unread
      // even though the fetch had brought back the corrected value.
      setItems(prev => {
        if (prev.length === 0) return incoming
        const same = prev.length === incoming.length &&
          prev.every((item, i) =>
            item.id === incoming[i].id &&
            item.updated_at === incoming[i].updated_at &&
            item.is_read === incoming[i].is_read &&
            item.is_starred === incoming[i].is_starred &&
            item.status === incoming[i].status &&
            item.thread_count === incoming[i].thread_count &&
            item.open_count === incoming[i].open_count)
        return same ? prev : incoming
      })
      setTotal(count || 0)
      setHasMore((count || 0) > incoming.length)
      // Cache the live window only — extras reload on demand.
      setCached(cacheKey, incoming.slice(0, pageSize))
      lastFetchedAt.current = Date.now()
    } catch (e) {
      if (stale()) return
      console.warn('useInboxIntelligence error:', e)
      // Offline-first: a hung/timed-out Supabase query falls through to the
      // PGlite mirror instead of an empty inbox. Only fall back when nothing is
      // already showing (cold cache) so a failed silent poll never replaces
      // good in-memory data. These rows are stale, so they are deliberately NOT
      // written back into the network cache (setCached) — the next live fetch
      // replaces them. Error state surfaces only when local is also empty.
      const cur = getCached(cacheKey)
      if (!cur || cur.length === 0) {
        try {
          const uid = resolvedUserIdRef.current || viewingUserId || null
          const local = await localFallback('email_intelligence', {
            where: uid ? 'user_id = $1' : '',
            params: uid ? [uid] : [],
            orderBy: 'sent_at DESC NULLS LAST',
            limit: 1000,
          })
          let rows
          if (filters.snoozed === true) {
            const snoozeData = _getSnoozed()
            const now = Date.now()
            rows = local
              .filter(r => snoozeData[r.id] && new Date(snoozeData[r.id]).getTime() > now)
              .map(r => ({ ...r, snoozed_until: snoozeData[r.id] }))
              .sort((a, b) => new Date(a.snoozed_until) - new Date(b.snoozed_until))
          } else {
            rows = filterSnoozed(applyLocalInboxFilters(local, filters))
          }
          if (rows.length > 0 && !stale()) {
            setItems(rows.slice(page * pageSize, (page + 1) * pageSize))
            setTotal(rows.length)
            setError(null)
            return
          }
        } catch (fallbackErr) {
          console.warn('useInboxIntelligence local fallback failed:', fallbackErr?.message)
        }
      }
      setError(e)
    } finally {
      if (!stale()) setLoading(false)
    }
  }, [JSON.stringify(filters), page, cacheKey, viewingUserId])

  useEffect(() => { fetch() }, [fetch])

  // Snooze changes (this tab or another) re-filter the list — without this
  // the dispatched chatrone:snooze-changed event had no listener and the
  // Snoozed view / inbox only resynced on the next poll.
  useEffect(() => {
    const onSnoozeChanged = () => fetch(true)
    window.addEventListener('chatrone:snooze-changed', onSnoozeChanged)
    return () => window.removeEventListener('chatrone:snooze-changed', onSnoozeChanged)
  }, [fetch])

  // Optimistic read-state patch — repaints the row the moment markAsRead /
  // markAsUnread is called, instead of after the API round-trip (which includes
  // a Gmail label write) plus Realtime plus a refetch. Also keeps the unread
  // badge honest. The next real fetch supersedes this.
  useEffect(() => {
    const onReadChanged = (e) => {
      const { id, isRead } = e.detail || {}
      if (!id) return
      setItems(prev => {
        const idx = prev.findIndex(i => i.id === id)
        if (idx === -1 || prev[idx].is_read === isRead) return prev
        const next = prev.slice()
        next[idx] = { ...next[idx], is_read: isRead }
        return next
      })
      setStats(s => ({ ...s, unread: Math.max(0, (s.unread || 0) + (isRead ? -1 : 1)) }))
    }
    window.addEventListener('chatrone:email-read-changed', onReadChanged)
    return () => window.removeEventListener('chatrone:email-read-changed', onReadChanged)
  }, [])

  // Paint cache-first once the persistent cache finishes hydrating. If this
  // folder mounted cold (post-reload, empty in-memory cache) and the first
  // fetch is slow or wedged, show whatever IndexedDB had for this exact folder
  // key as soon as it's available — skeletons never outlive the cache. The
  // in-flight fetch still replaces this with fresh rows when it settles.
  useEffect(() => {
    const onHydrated = () => {
      const c = getCached(cacheKey)
      if (Array.isArray(c) && c.length) {
        setItems(prev => (prev.length ? prev : c))
        setLoading(false)
      }
    }
    window.addEventListener('chatrone:cache-hydrated', onHydrated)
    return () => window.removeEventListener('chatrone:cache-hydrated', onHydrated)
  }, [cacheKey])

  // Supabase Realtime — instant refresh when new emails are analyzed.
  // Both INSERT and UPDATE events fire the same debounced fetch so bursts
  // collapse to a single list refresh (e.g. a cron re-analyzing a batch
  // can emit 20 UPDATE events in the same second — without debounce that
  // would fire 20 sequential SELECTs per open tab).
  //
  // Channel name must be unique per hook INSTANCE. Supabase keys channels
  // by name — if this hook is mounted in two places simultaneously (e.g.
  // the inbox list + the follow-up queue widget), the second `.channel()`
  // call on the same name throws "already subscribed". useId() gives a
  // stable, unique-per-mount string that won't collide.
  const instanceId = useId()
  const debouncedSilentFetch = useDebouncedCallback(() => fetch(true), REALTIME_DEBOUNCE_MS)
  useEffect(() => {
    const uid = resolvedUserIdRef.current
    const userFilter = uid ? `user_id=eq.${uid}` : undefined
    const channel = supabase
      .channel(`email-intelligence-changes:${instanceId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'email_intelligence',
        ...(userFilter ? { filter: userFilter } : {}),
      }, (payload) => {
        debouncedSilentFetch()
        // Desktop notification for new focused emails (not from yourself)
        const email = payload.new
        if (email && !email.is_outbound && email.category === 'focused') {
          fireDesktopNotification(email)
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'email_intelligence',
        ...(userFilter ? { filter: userFilter } : {}),
      }, debouncedSilentFetch)
      .subscribe()

    return () => {
      debouncedSilentFetch.cancel()
      supabase.removeChannel(channel)
    }
  }, [debouncedSilentFetch])

  // Silent background refresh: polls every 60s + refetches on tab focus.
  //
  // When the tab regains focus we also kick Gmail (via /api/poll-inbox) on a
  // debounced schedule so emails that arrived while the tab was hidden land
  // immediately — not up to a minute later when the next cron fires.
  // Debounce prevents thrashing the endpoint when users rapid-switch tabs.
  const lastPollRef = useRef(0)
  const lastFetchedAt = useRef(0)

  // On initial mount, fire a Gmail poll immediately so new emails that arrived
  // since the last cron tick land right away (rather than waiting up to 30s).
  // Then re-fetch once the poll resolves so the very first view reflects the
  // just-pulled mail. Without this repaint the fresh rows are invisible until a
  // background tick — and silentRefetch skips any fetch within 60s of the
  // initial load, so "open on the most recent emails" could lag ~60s.
  useEffect(() => {
    if (viewingUserId) return // don't poll other users' Gmail from this session
    const now = Date.now()
    if (now - lastPollRef.current > 30_000) {
      lastPollRef.current = now
      triggerPoll()
        .then(() => { lastFetchedAt.current = Date.now(); return fetch(true) })
        .catch(() => {})
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const silentRefetch = useCallback(async () => {
    // Skip fetch if data was refreshed within the last 60 seconds — prevents
    // hammering the DB when users rapidly switch tabs or windows.
    if (Date.now() - lastFetchedAt.current < 60_000) return

    // Viewing-as mode (e.g. Janet's mailbox) — don't try to poll someone
    // else's Gmail from this user's session; just refetch locally.
    if (!viewingUserId && !document.hidden) {
      const now = Date.now()
      if (now - lastPollRef.current > 30_000) {
        lastPollRef.current = now
        // Fire-and-forget — if it fails, the local refetch below still runs
        triggerPoll().catch(() => {})
      }
    }
    return fetch(true)
  }, [fetch, viewingUserId])
  useAutoRefresh(silentRefetch, { intervalMs: 30_000 })

  // Load the next (older) chunk via keyset pagination — sent_at strictly
  // before the current oldest row, so a shifting page-0 window (new mail
  // arriving) can never produce gaps or duplicates.
  const loadMore = useCallback(async () => {
    if (loadingMore) return
    const startedFor = cacheKey
    const current = itemsRef.current
    const last = current[current.length - 1]
    if (!last?.sent_at) { setHasMore(false); return }
    setLoadingMore(true)
    try {
      const userId = resolvedUserIdRef.current
      const dbSnooze = await snoozeColumnAvailable()
      const q = buildListQuery(userId, filters, dbSnooze, !!viewingUserId)
        .lt('sent_at', last.sent_at)
        .limit(pageSize)
      const { data, error: err } = await q
      // The view changed while this page was loading — its rows belong to the
      // old view and must not be appended to the new one.
      if (startedFor !== viewKeyRef.current) return
      if (err) throw err
      let rows = data || []
      if (!filters.search) rows = filterSnoozed(rows)
      const seen = new Set(current.map(i => i.id))
      const fresh = rows.filter(r => !seen.has(r.id))
      extraRef.current = [...extraRef.current, ...fresh]
      if (fresh.length > 0) setItems(prev => [...prev, ...fresh])
      setHasMore((data || []).length >= pageSize)
    } catch (e) {
      console.warn('loadMore failed:', e?.message)
    } finally {
      setLoadingMore(false)
    }
  }, [JSON.stringify(filters), pageSize, loadingMore, cacheKey]) // eslint-disable-line react-hooks/exhaustive-deps

  return { items, total, loading, error, stats, refetch: fetch, loadMore, hasMore, loadingMore }
}

// ─── Action helpers (POST to API routes) ────────────────────────────────────

// The raw server call. Kept pure so the mutationQueue can call it directly
// on drain (retries without going back through the resilient wrapper, which
// would re-enqueue on failure and loop).
// Actions that actually send/create something external and must NOT be silently
// replayed — a timeout might fire AFTER the server already sent the email, so
// auto-queueing would deliver a duplicate. These surface the error instead.
const NON_IDEMPOTENT_ACTIONS = new Set(['send_reply'])

export async function _rawPostInboxAction(action, payload, timeoutMs = 45000) {
  const headers = await getAuthHeaders()
  // Bound the request so a hung fetch (slow Gmail send, half-open socket after
  // sleep) can never leave the UI stuck on "Sending…" forever.
  const controller = new AbortController()
  const timer = setTimeout(
    () => controller.abort(new DOMException('inbox-action timeout', 'TimeoutError')),
    timeoutMs
  )
  let res
  try {
    res = await window.fetch('/api/inbox-actions', {
      method: 'POST',
      headers,
      body: JSON.stringify({ action, ...payload }),
      signal: controller.signal,
    })
  } finally {
    clearTimeout(timer)
  }
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `HTTP ${res.status} — Action ${action} failed`)
  }
  return await res.json()
}

// Resilient wrapper. Tries the server first; on network failure (offline,
// timeout, abort) it falls back to the mutation queue so the action isn't
// lost. User-visible behavior matches the old postAction for normal flows.
//
// Actions that never want queueing (e.g. anything expecting a response body)
// can call _rawPostInboxAction directly.
async function postAction(action, payload) {
  try {
    return await _rawPostInboxAction(action, payload)
  } catch (e) {
    console.warn(`inbox-actions/${action} error:`, e.message)
    // Non-idempotent actions (send_reply) are NEVER auto-queued: a timeout can
    // fire after the server already sent, so replaying would double-send. Let
    // the caller surface the failure so the user can check Sent before retrying.
    if (NON_IDEMPOTENT_ACTIONS.has(action)) throw e
    // Network-ish errors → queue for later. TypeError covers most fetch
    // failures (offline, DNS, CORS); AbortError/TimeoutError covers timeouts.
    // A real server 400/403/etc already succeeded at network level and
    // shouldn't be queued (it'd just fail again).
    if (e?.name === 'TypeError' || e?.name === 'AbortError' || e?.name === 'TimeoutError' ||
        (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      try {
        const { enqueue } = await import('../lib/mutationQueue')
        await enqueue(action, payload)
        // Queue persisted — resolve as if successful so caller UI flows
        // continue (optimistic). Real retry happens on next online/focus.
        return { queued: true }
      } catch (qErr) {
        console.warn('[mutationQueue] enqueue fallback failed:', qErr.message)
      }
    }
    throw e
  }
}

export async function confirmLinks(id) {
  return postAction('confirm_links', { intelligence_id: id })
}

// ─── Gmail parity: starring + labeling ──────────────────────────────────────
// Routes through /api/inbox-actions so Gmail is updated in sync with Supabase.

export async function setStarred(id, isStarred) {
  return postAction('set_starred', { intelligence_id: id, starred: !!isStarred })
}

export async function setLabels(id, labels) {
  const clean = Array.from(new Set((labels || [])
    .map(l => (l || '').trim())
    .filter(Boolean)))
  const { error } = await supabase
    .from('email_intelligence')
    .update({ labels: clean })
    .eq('id', id)
  if (error) throw error
}

// Collect the distinct labels currently in use across the user's inbox, so
// the label picker can offer recent picks instead of re-typing every time.
export async function listUsedLabels() {
  const userId = await getCurrentUserId()
  let q = supabase
    .from('email_intelligence')
    .select('labels')
    .not('labels', 'is', null)
    .limit(500)
  if (userId) q = q.eq('user_id', userId)
  const { data } = await q
  const set = new Set()
  for (const row of (data || [])) {
    for (const l of (row.labels || [])) if (l) set.add(l)
  }
  return Array.from(set).sort()
}

export async function updateDraft(id, text) {
  return postAction('update_draft', { intelligence_id: id, draft_reply: text })
}

export async function sendReply(id, { archiveAfterSend = false, to, cc, bcc, verifyFirst = false } = {}) {
  // verifyFirst tells the server to check Gmail's Sent folder before sending —
  // set only on a retry, so the common first send pays no extra latency.
  return postAction('send_reply', { intelligence_id: id, archiveAfterSend, to, cc, bcc, verifyFirst })
}

export async function confirmTask(id, index, overrides = {}) {
  // overrides: { title, due_date, quadrant, assigned_to: uuid[] } — delegation
  // and modal edits travel to the server instead of being silently dropped
  return postAction('confirm_task', { intelligence_id: id, action_index: index, ...overrides })
}

export async function dismissTask(id, index) {
  return postAction('dismiss_task', { intelligence_id: id, action_index: index })
}

export async function dismissEmail(id) {
  return postAction('dismiss_email', { intelligence_id: id })
}

// ─── Follow-Up triage actions ───────────────────────────────────────────────

export async function followUpAccept(id, { quadrant, title, due_date } = {}) {
  return postAction('follow_up_accept', {
    intelligence_id: id, quadrant, title, due_date,
  })
}

export async function followUpHandled(id, note) {
  return postAction('follow_up_handled', { intelligence_id: id, note })
}

export async function followUpDelegate(id, assigneeUserId, { quadrant, title, due_date, note } = {}) {
  return postAction('follow_up_delegate', {
    intelligence_id: id,
    assignee_user_id: assigneeUserId,
    quadrant, title, due_date, note,
  })
}

export async function followUpDismiss(id) {
  return postAction('follow_up_dismiss', { intelligence_id: id })
}

export async function followUpFyi(id) {
  return postAction('follow_up_fyi', { intelligence_id: id })
}

export async function setSenderVip(id, remove = false) {
  return postAction('set_sender_vip', { intelligence_id: id, remove })
}

// ─── Archive / Trash / Bulk — routed through API for Gmail sync ─────────────

export async function archiveEmail(id) {
  if (!id) return
  return postAction('archive_email', { intelligence_id: id })
}

export async function unarchiveEmail(id) {
  if (!id) return
  return postAction('unarchive_email', { intelligence_id: id })
}

export async function trashEmail(id) {
  if (!id) return
  return postAction('trash_email', { intelligence_id: id })
}

export async function bulkArchive(ids) {
  if (!ids?.length) return
  // Fire in parallel — each syncs to Gmail individually
  await Promise.all(ids.map(id => postAction('archive_email', { intelligence_id: id })))
}

export async function bulkDelete(ids) {
  if (!ids?.length) return
  await Promise.all(ids.map(id => postAction('trash_email', { intelligence_id: id })))
}

export async function bulkMarkRead(ids) {
  if (!ids?.length) return
  await Promise.all(ids.map(id => postAction('mark_read', { intelligence_id: id })))
}

export async function bulkMarkUnread(ids) {
  if (!ids?.length) return
  await Promise.all(ids.map(id => postAction('mark_unread', { intelligence_id: id })))
}

export async function bulkSetStarred(ids, isStarred) {
  if (!ids?.length) return
  await Promise.all(ids.map(id => postAction('set_starred', { intelligence_id: id, starred: !!isStarred })))
}

export async function bulkLink(ids, entityType, entityId) {
  if (!ids?.length || !entityType || !entityId) return
  return postAction('bulk_link', { intelligence_ids: [...ids], entity_type: entityType, entity_id: entityId })
}

export async function regenerateDraft(id, tone, { replaceEdited = false } = {}) {
  return postAction('regenerate_draft', { intelligence_id: id, tone, ...(replaceEdited ? { replace_edited: true } : {}) })
}

// Resolve once the browser is idle (or after a short delay where
// requestIdleCallback isn't available), so background prefetching yields to the
// list query and to rendering.
function whenIdle(timeout = 2000) {
  return new Promise(resolve => {
    if (typeof window === 'undefined') return resolve()
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => resolve(), { timeout })
    } else {
      setTimeout(resolve, 250)
    }
  })
}

// ─── Email body fetch (from saved_emails) ───────────────────────────────────

// Cache-first. Hits entityCache for instant render; network fetch only when
// missing. Callers pass { force: true } to bypass cache (e.g. after a server
// modification invalidates the cached copy).
export async function fetchEmailBody(gmailMessageId, { force = false } = {}) {
  if (!gmailMessageId) return null
  if (!force) {
    const cached = entityCache.get('body', gmailMessageId)
    if (cached !== undefined) return cached
  }
  const { data } = await supabase
    .from('saved_emails')
    .select('body_text, body_html, to_emails, cc_emails, sent_at, attachments')
    .eq('gmail_message_id', gmailMessageId)
    .single()
  const result = data || null
  // Cache both hits AND misses (null) — missed lookups shouldn't re-hit
  // the server every time. A later poll-inbox run will populate them.
  entityCache.set('body', gmailMessageId, result)
  return result
}

// Prefetch N email bodies in a single round trip, then seed the cache.
// Non-blocking — call from list render, don't await. Returns a Promise so
// tests / callers can await if needed. Already-cached ids are skipped.
//
// body_html is capped at 200 KB per message, and real mail regularly runs
// 30–150 KB, so a wide prefetch is genuinely expensive: 15 ids measured at
// ~1 MB in one request. That request competed with the list query itself,
// which is what "buffering very slowly" looked like on a cold folder. Two
// guards now: callers ask for fewer ids, and the work is deferred to browser
// idle time so it can never delay the first paint.
const PREFETCH_BATCH = 8

export async function prefetchEmailBodies(gmailMessageIds = []) {
  const missing = (gmailMessageIds || [])
    .filter(Boolean)
    .filter(id => !entityCache.has('body', id))
    .slice(0, PREFETCH_BATCH)
  if (missing.length === 0) return { fetched: 0 }
  await whenIdle()
  try {
    const { data } = await supabase
      .from('saved_emails')
      .select('gmail_message_id, body_text, body_html, to_emails, cc_emails, sent_at, attachments')
      .in('gmail_message_id', missing)
    const byId = new Map((data || []).map(r => [r.gmail_message_id, r]))
    for (const id of missing) {
      // Seed both hits and misses — a miss today may become a hit after the
      // next poll-inbox tick but shouldn't generate a request until then.
      entityCache.set('body', id, byId.get(id) || null)
    }
    return { fetched: missing.length }
  } catch (err) {
    console.warn('[prefetchEmailBodies] failed:', err.message)
    return { fetched: 0, error: err.message }
  }
}

// ─── Thread fetch ───────────────────────────────────────────────────────────

export async function fetchThread(gmailThreadId, { force = false } = {}) {
  if (!gmailThreadId) return []
  if (!force) {
    const cached = entityCache.get('thread', gmailThreadId)
    if (cached !== undefined) return cached
  }
  const { data: emails } = await supabase
    .from('saved_emails')
    .select('id, gmail_message_id, gmail_thread_id, subject, sender_email, sender_name, body_text, body_html, sent_at, to_emails, cc_emails, attachments')
    .eq('gmail_thread_id', gmailThreadId)
    .order('sent_at', { ascending: true })

  const messageIds = (emails || []).map(e => e.gmail_message_id).filter(Boolean)
  let result = emails || []
  if (messageIds.length > 0) {
    const { data: intel } = await supabase
      .from('email_intelligence')
      .select('gmail_message_id, urgency, email_type, summary, priority_score')
      .in('gmail_message_id', messageIds)
    const intelMap = Object.fromEntries((intel || []).map(i => [i.gmail_message_id, i]))
    result = (emails || []).map(e => ({ ...e, intelligence: intelMap[e.gmail_message_id] || null }))
  }
  entityCache.set('thread', gmailThreadId, result)
  // Also seed body cache for each message — they're already fetched.
  for (const msg of result) {
    if (msg.gmail_message_id) {
      entityCache.set('body', msg.gmail_message_id, {
        body_text: msg.body_text,
        body_html: msg.body_html,
        to_emails: msg.to_emails,
        cc_emails: msg.cc_emails,
        sent_at: msg.sent_at,
      })
    }
  }

  // Lazy thread gap-fill: fire-and-forget check against Gmail to find any
  // messages in this thread that aren't in saved_emails yet. Only runs when
  // the thread has at least one message (otherwise we have no thread context).
  // Results appear on the next time the thread is opened (cache is cleared below).
  // iCloud threads have no Gmail counterpart — skip the Gmail gap-fill (the
  // 'icloud:'-prefixed id isn't a real Gmail thread and would just 404).
  if (result.length > 0 && !String(gmailThreadId).startsWith('icloud:')) {
    _syncThreadWithGmail(gmailThreadId, force)
  }

  return result
}

// Track which threads we've already synced this session so we don't hammer
// Gmail on every open. Cleared on page reload.
const _syncedThreads = new Set()

async function _syncThreadWithGmail(gmailThreadId, force = false) {
  if (!force && _syncedThreads.has(gmailThreadId)) return
  _syncedThreads.add(gmailThreadId)
  try {
    const sessionResult = await getSessionWithTimeout(3000)
    if (sessionResult.timedOut) return
    const token = sessionResult.data?.session?.access_token
    if (!token) return
    const res = await fetch('/api/gmail-sync-thread', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ threadId: gmailThreadId }),
    })
    if (!res.ok) return
    const data = await res.json()
    if (data.synced > 0) {
      // New messages found — bust the cache and notify the thread view to refetch
      entityCache.remove('thread', gmailThreadId)
      window.dispatchEvent(new CustomEvent('chatrone:thread-synced', { detail: { gmailThreadId } }))
      console.info(`[fetchThread] synced ${data.synced} missing message(s) for thread ${gmailThreadId}`)
    }
  } catch { /* non-fatal — never block thread display */ }
}

// Synchronous cached-body lookup — returns undefined on miss, the body
// object on hit (or null if we previously cached a miss). Used by the
// detail view to render instantly without a loading flicker when the body
// has already been prefetched.
export function getCachedEmailBody(gmailMessageId) {
  if (!gmailMessageId) return undefined
  return entityCache.get('body', gmailMessageId)
}

// Invalidate a cached body — call after server-side modification (e.g. Gmail
// sync updated the body, or the user edited a draft). The next fetchEmailBody
// will re-hit the server.
export function invalidateEmailBody(gmailMessageId) {
  entityCache.remove('body', gmailMessageId)
}

export function invalidateThread(gmailThreadId) {
  entityCache.remove('thread', gmailThreadId)
}

// ─── Mark as read / unread — synced to Gmail ────────────────────────────────

// Optimistic read-state broadcast. The server round-trip includes a Gmail label
// write, so waiting for it (and then for Realtime + a refetch) left the row
// looking unread for a second or more. Every mounted list patches its own copy
// immediately off this event; the authoritative value still arrives on the next
// fetch and overwrites it.
function announceReadChange(id, isRead) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent('chatrone:email-read-changed', { detail: { id, isRead } }))
}

export async function markAsRead(id) {
  if (!id) return
  announceReadChange(id, true)
  return postAction('mark_read', { intelligence_id: id }).catch(e => {
    console.warn('markAsRead error:', e.message)
    announceReadChange(id, false)  // roll back the optimistic paint
  })
}

export async function markAsUnread(id) {
  if (!id) return
  announceReadChange(id, false)
  return postAction('mark_unread', { intelligence_id: id }).catch(e => {
    console.warn('markAsUnread error:', e.message)
    announceReadChange(id, true)
  })
}

// ─── Trigger poll / analysis (manual sync) ──────────────────────────────────

export async function triggerPoll() {
  try {
    const headers = await getAuthHeaders()
    const res = await window.fetch('/api/poll-inbox', {
      method: 'POST',
      headers,
      body: JSON.stringify({ manual: true }),
    })
    if (!res.ok) throw new Error(`poll-inbox failed (${res.status})`)
    return await res.json()
  } catch (e) {
    console.warn('triggerPoll error:', e.message)
    throw e
  }
}

export async function triggerAnalysis() {
  try {
    const headers = await getAuthHeaders()
    const res = await window.fetch('/api/process-inbox', {
      method: 'POST',
      headers,
      body: JSON.stringify({ manual: true }),
    })
    if (!res.ok) throw new Error(`process-inbox failed (${res.status})`)
    return await res.json()
  } catch (e) {
    console.warn('triggerAnalysis error:', e.message)
    throw e
  }
}

// ─── Gmail Search (full mailbox via API passthrough) ────────────────────────

export async function searchGmail(query, maxResults = 20, pageToken = null) {
  const headers = await getAuthHeaders()
  const res = await window.fetch('/api/gmail-search', {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, maxResults, pageToken }),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `Gmail search failed (${res.status})`)
  }
  return await res.json()
}

// ─── Gmail Fetch (single email, caches to saved_emails) ────────────────────

export async function fetchGmailEmail(gmailMessageId) {
  const headers = await getAuthHeaders()
  const res = await window.fetch('/api/gmail-fetch', {
    method: 'POST',
    headers,
    body: JSON.stringify({ gmail_message_id: gmailMessageId }),
  })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `Gmail fetch failed (${res.status})`)
  }
  return await res.json()
}

// ─── Unified Follow-Up Queue (emails + submissions) ────────────────────────

export function useFollowUpQueue({ emailLimit = 10, submissionLimit = 10 } = {}) {
  const instanceId = useId()
  const [emails, setEmails] = useState([])
  const [submissions, setSubmissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [counts, setCounts] = useState({ emails: 0, submissions: 0 })
  const [urgencyStats, setUrgencyStats] = useState({
    respondToday: 0, respondThisWeek: 0, review: 0, fyi: 0,
  })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const userId = await getCurrentUserId()
      const todayStr = new Date().toISOString().split('T')[0]

      const [emailRes, subRes, countRes, statsRes] = await Promise.all([
        supabase
          .from('email_intelligence')
          .select('id, subject, from_name, from_email, sent_at, flag_color, urgency, summary, triage_status')
          .eq('triage_status', 'pending_review')
          .eq('user_id', userId || '00000000-0000-0000-0000-000000000000')
          .order('sent_at', { ascending: false, nullsFirst: false })
          .limit(emailLimit),
        supabase
          .from('submissions')
          .select('*')
          .lte('follow_up_date', todayStr)
          .order('follow_up_date', { ascending: true })
          .limit(submissionLimit),
        supabase
          .from('email_intelligence')
          .select('id', { count: 'exact', head: true })
          .eq('triage_status', 'pending_review')
          .eq('user_id', userId || '00000000-0000-0000-0000-000000000000'),
        supabase.rpc('get_inbox_stats', { p_user_id: userId }),
      ])

      setEmails(emailRes.data || [])
      setSubmissions(subRes.data || [])
      setCounts({
        emails: countRes.count || (emailRes.data || []).length,
        submissions: (subRes.data || []).length,
      })
      if (statsRes.data) {
        setUrgencyStats({
          respondToday: statsRes.data.respondToday || 0,
          respondThisWeek: statsRes.data.respondThisWeek || 0,
          review: statsRes.data.review || 0,
          fyi: statsRes.data.fyi || 0,
        })
      }
    } catch {
      setEmails([])
      setSubmissions([])
    } finally {
      setLoading(false)
    }
  }, [emailLimit, submissionLimit])

  useEffect(() => { load() }, [load])

  // Realtime: re-run whenever an email_intelligence row changes.
  // Debounced so bursts (newsletter digest, cron batch) collapse to one load.
  const debouncedLoad = useDebouncedCallback(load, REALTIME_DEBOUNCE_MS)
  useEffect(() => {
    const ch = supabase
      .channel(`follow-up-queue:${instanceId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'email_intelligence' },
        debouncedLoad)
      .subscribe()
    return () => {
      debouncedLoad.cancel()
      supabase.removeChannel(ch)
    }
  }, [debouncedLoad])

  return { emails, submissions, counts, urgencyStats, loading, refetch: load }
}

// ─── User profiles (for delegate picker) ────────────────────────────────────

export async function fetchTeamMembers() {
  try {
    // user_profiles has no first_name/last_name columns — only full_name.
    // Derive them here so every consumer's `m.first_name || m.full_name`
    // fallback chain keeps working with the short name it expects.
    const { data } = await supabase
      .from('user_profiles')
      .select('id, full_name, email, role')
      .not('role', 'is', null)
      .order('full_name', { ascending: true })
    return (data || []).map((m) => {
      const parts = (m.full_name || '').trim().split(/\s+/).filter(Boolean)
      return {
        ...m,
        first_name: parts[0] || null,
        last_name: parts.length > 1 ? parts[parts.length - 1] : null,
      }
    })
  } catch {
    return []
  }
}

// ─── Gmail Attachment Download ──────────────────────────────────────────────

export async function downloadAttachment(gmailMessageId, attachmentId, filename) {
  const headers = await getAuthHeaders()
  const res = await window.fetch('/api/gmail-attachment', {
    method: 'POST',
    headers,
    body: JSON.stringify({ gmail_message_id: gmailMessageId, attachment_id: attachmentId, filename }),
  })
  if (!res.ok) throw new Error('Attachment download failed')
  const data = await res.json()

  // Convert base64 to blob and trigger browser download
  const binaryStr = atob(data.data)
  const bytes = new Uint8Array(binaryStr.length)
  for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i)
  const blob = new Blob([bytes])
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = data.filename || filename || 'attachment'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
